import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { 
  Home,
  BookOpen, 
  FlaskConical, 
  Calculator, 
  CalendarDays,
  Rocket, 
  Menu, 
  X,
  Atom,
} from "lucide-react";

const NAV_ITEMS = [
  { key: "home", path: "/", icon: Home },
  { key: "whatArePeptides", path: "/what-are-peptides", icon: Atom },
  { key: "learnPeptides", path: "/learn", icon: BookOpen },
  { key: "reconstitution", path: "/reconstitution", icon: FlaskConical },
  { key: "calculator", path: "/calculator", icon: Calculator },
  { key: "schedule", path: "/schedule", icon: CalendarDays },
  { key: "getStarted", path: "/get-started", icon: Rocket },
];

// Navigation link that scrolls to top
const NavLink = ({ to, children, className, onClick }) => {
  const navigate = useNavigate();
  
  const handleClick = (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'instant' });
    if (onClick) onClick();
    navigate(to);
  };
  
  return (
    <a href={to} onClick={handleClick} className={className}>
      {children}
    </a>
  );
};

export const Navigation = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <nav className="sticky top-0 z-50 w-full border-b border-border/50 bg-background/95 backdrop-blur-sm">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo / Brand - Custom SVG Logo */}
          <NavLink to="/" className="flex items-center group">
            <img 
              src="/lumon-logo.svg" 
              alt="Lumon Peptides" 
              className="h-7 sm:h-8 w-auto transition-opacity group-hover:opacity-80"
            />
          </NavLink>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <NavLink key={item.path} to={item.path}>
                  <Button
                    variant={isActive ? "default" : "ghost"}
                    size="sm"
                    className={`
                      gap-1.5 transition-all duration-200
                      ${isActive 
                        ? "bg-primary text-primary-foreground" 
                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                      }
                    `}
                  >
                    <Icon className="w-4 h-4" />
                    {t(`nav.${item.key}`)}
                  </Button>
                </NavLink>
              );
            })}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-2 lg:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-border/50">
            <div className="flex flex-col gap-2">
              {NAV_ITEMS.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                
                return (
                  <NavLink 
                    key={item.path} 
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Button
                      variant={isActive ? "default" : "ghost"}
                      size="sm"
                      className={`
                        w-full justify-start gap-2 transition-all duration-200
                        ${isActive 
                          ? "bg-primary text-primary-foreground" 
                          : "text-muted-foreground hover:text-foreground hover:bg-muted"
                        }
                      `}
                    >
                      <Icon className="w-4 h-4" />
                      {t(`nav.${item.key}`)}
                    </Button>
                  </NavLink>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
